<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useCommunicationErrorMessage } from 'dashboard/composables/useCommunicationErrorMessage';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const { friendlyMessage } = useCommunicationErrorMessage();

const connection = computed(
  () => getters['communicationConnection/getCommunicationConnection'].value
);
const connectionUiFlags = computed(
  () => getters['communicationConnection/getUIFlags'].value
);
const accounts = computed(
  () => getters['communicationConnection/getAccounts'].value || []
);
const inboxes = computed(() => getters['inboxes/getInboxes'].value || []);

const isConfigured = computed(() => !!connection.value?.id);

const evolutionInboxes = computed(() =>
  inboxes.value.filter(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' &&
      inbox.provider === 'evolution_gateway'
  )
);

const rows = computed(() =>
  evolutionInboxes.value.map(inbox => {
    const account = accounts.value.find(
      a => a.phone_number === inbox.phone_number
    );
    return { inbox, account };
  })
);

const boundPhoneNumbers = computed(
  () => new Set(evolutionInboxes.value.map(i => i.phone_number).filter(Boolean))
);

const headers = computed(() => [
  t('COMMUNICATION_CONNECTION.INBOX_MAPPING.TABLE.INBOX'),
  t('COMMUNICATION_CONNECTION.INBOX_MAPPING.TABLE.ACCOUNT'),
  t('COMMUNICATION_CONNECTION.INBOX_MAPPING.TABLE.PROVIDER'),
  t('COMMUNICATION_CONNECTION.INBOX_MAPPING.TABLE.PHONE_NUMBER'),
  t('COMMUNICATION_CONNECTION.INBOX_MAPPING.TABLE.STATUS'),
  t('COMMUNICATION_CONNECTION.INBOX_MAPPING.TABLE.ACTIONS'),
]);

const loadData = async () => {
  if (!isConfigured.value) return;
  try {
    await Promise.all([
      store.dispatch('communicationConnection/fetchAccounts'),
      store.dispatch('inboxes/get'),
    ]);
  } catch (error) {
    useAlert(friendlyMessage(error));
  }
};

const { startRefetching } = useLiveRefresh(loadData, 60000);

onMounted(async () => {
  await store.dispatch('communicationConnection/get');
  await loadData();
  startRefetching();
});

// Rebind dialog
const rebindDialogRef = ref(null);
const rebindTarget = ref(null);
const rebindSelection = ref('');

const rebindOptions = computed(() => {
  if (!rebindTarget.value) return [];
  return accounts.value
    .filter(a => a.connection_status === 'open' && a.phone_number)
    .filter(
      a =>
        a.gateway_account_id !== rebindTarget.value.account?.gateway_account_id
    )
    .filter(a => !boundPhoneNumbers.value.has(a.phone_number))
    .map(a => ({
      value: a.gateway_account_id,
      label: `${a.gateway_instance_name} (${a.phone_number})`,
    }));
});

const openRebind = row => {
  rebindTarget.value = row;
  rebindSelection.value = '';
  rebindDialogRef.value?.open();
};

const confirmRebind = async () => {
  if (!rebindSelection.value) return;
  try {
    await store.dispatch('communicationConnection/rebind', {
      channelId: rebindTarget.value.inbox.channel_id,
      gatewayAccountId: rebindSelection.value,
    });
    useAlert(t('COMMUNICATION_CONNECTION.INBOX_MAPPING.REBIND.SUCCESS'));
    rebindDialogRef.value?.close();
    await loadData();
  } catch (error) {
    useAlert(friendlyMessage(error));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="connectionUiFlags.isFetching"
    :loading-message="t('COMMUNICATION_CONNECTION.INBOX_MAPPING.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('COMMUNICATION_CONNECTION.INBOX_MAPPING.HEADER')"
        :description="t('COMMUNICATION_CONNECTION.INBOX_MAPPING.DESCRIPTION')"
      />
      <div class="flex gap-4 mt-2 text-sm">
        <router-link
          :to="{ name: 'communication_connection_index' }"
          class="text-n-blue-11 hover:underline"
        >
          {{ t('COMMUNICATION_CONNECTION.NAV.SETTINGS') }}
        </router-link>
        <router-link
          :to="{ name: 'communication_whatsapp_accounts' }"
          class="text-n-blue-11 hover:underline"
        >
          {{ t('COMMUNICATION_CONNECTION.NAV.WHATSAPP_ACCOUNTS') }}
        </router-link>
        <router-link
          :to="{ name: 'communication_inbox_mapping' }"
          class="text-n-blue-11 font-medium"
        >
          {{ t('COMMUNICATION_CONNECTION.NAV.INBOX_MAPPING') }}
        </router-link>
      </div>
    </template>
    <template #body>
      <p v-if="!isConfigured" class="text-sm text-n-slate-11">
        {{ t('COMMUNICATION_CONNECTION.INBOX_MAPPING.NOT_CONFIGURED') }}
      </p>
      <BaseTable
        v-else
        :headers="headers"
        :items="rows"
        :loading="connectionUiFlags.isFetchingAccounts"
        :no-data-message="t('COMMUNICATION_CONNECTION.INBOX_MAPPING.EMPTY')"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="row in items" :key="row.inbox.id" :item="row">
            <BaseTableCell class="font-medium text-n-slate-12">
              {{ row.inbox.name }}
            </BaseTableCell>
            <BaseTableCell>
              {{ row.account?.gateway_instance_name || '—' }}
            </BaseTableCell>
            <BaseTableCell>
              {{
                t('COMMUNICATION_CONNECTION.CONVERSATION_PANEL.PROVIDER_VALUE')
              }}
            </BaseTableCell>
            <BaseTableCell>{{ row.inbox.phone_number || '—' }}</BaseTableCell>
            <BaseTableCell>
              <span
                v-if="row.account"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="
                  row.account.connection_status === 'open'
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-ruby-3 text-n-ruby-11'
                "
              >
                {{ row.account.connection_status }}
              </span>
              <span v-else class="text-n-slate-10">—</span>
            </BaseTableCell>
            <BaseTableCell>
              <NextButton
                faded
                blue
                xs
                :label="
                  t('COMMUNICATION_CONNECTION.INBOX_MAPPING.REBIND.BUTTON')
                "
                @click="openRebind(row)"
              />
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>

      <Dialog
        ref="rebindDialogRef"
        type="edit"
        :title="
          rebindTarget
            ? t('COMMUNICATION_CONNECTION.INBOX_MAPPING.REBIND.DIALOG_TITLE', {
                name: rebindTarget.inbox.name,
              })
            : ''
        "
        :description="
          t('COMMUNICATION_CONNECTION.INBOX_MAPPING.REBIND.DIALOG_DESCRIPTION')
        "
        :is-loading="connectionUiFlags.isRebinding"
        :confirm-button-label="
          t('COMMUNICATION_CONNECTION.INBOX_MAPPING.REBIND.CONFIRM')
        "
        :cancel-button-label="
          t('COMMUNICATION_CONNECTION.INBOX_MAPPING.REBIND.CANCEL')
        "
        :disable-confirm-button="!rebindSelection"
        @confirm="confirmRebind"
      >
        <div>
          <label class="text-sm text-n-slate-12 mb-1 block">
            {{
              t('COMMUNICATION_CONNECTION.INBOX_MAPPING.REBIND.SELECT_LABEL')
            }}
          </label>
          <Select
            v-model="rebindSelection"
            :options="rebindOptions"
            :placeholder="
              t(
                'COMMUNICATION_CONNECTION.INBOX_MAPPING.REBIND.SELECT_PLACEHOLDER'
              )
            "
          />
          <p
            v-if="rebindOptions.length === 0"
            class="text-xs text-n-slate-10 mt-2"
          >
            {{
              t(
                'COMMUNICATION_CONNECTION.INBOX_MAPPING.REBIND.NO_OTHER_ACCOUNTS'
              )
            }}
          </p>
        </div>
      </Dialog>
    </template>
  </SettingsLayout>
</template>
